import React from "react";
import {
  CLOTHING_SUBCATEGORIES,
  CLOTHING_GENDERS,
  CLOTHING_SIZE_BANDS,
  CLOTHING_FOOTWEAR_SIZES,
  CLOTHING_BRANDS,
} from "@/lib/marketplace";

const slug = (s) => String(s).replace(/[^a-zA-Z0-9]+/g, "-");

/**
 * ClothingPickers — Iteration 301. The 4 clothing sub-category chip
 * groups, shared by the Kid Zone sell form, the parent /sell form and
 * the Edit Listing modal so every surface stays in perfect parity:
 *   1. What kind of clothing? (Tops … Bundles)
 *   2. Who is it for?         (Boys / Girls / Unisex / Baby 0-24M)
 *   3. Size                   (Newborn → 14Y+; UK footwear ladder for shoes)
 *   4. Brand                  (Next, M&S, Boden, Zara, H&M, Tu, George,
 *                              Nutmeg, Nike, Adidas, Other)
 *
 * Tap-chips only — no free text. Tapping a picked chip unpicks it;
 * switching kind resets size (footwear uses a different ladder).
 *
 * Props:
 *   values: { clothing_subcategory, clothing_gender, clothing_size, clothing_brand }
 *   onChange(updates) — partial-merge object, e.g. { clothing_size: "3-4Y" }
 *   testIdPrefix — "kid-form-clothing" | "sell-clothing" | "edit-listing-clothing"
 */
export default function ClothingPickers({ values, onChange, testIdPrefix }) {
  const chipCls = (picked) =>
    `px-3 py-1.5 rounded-full text-xs font-bold border transition ${picked ? "bg-brandPink text-white border-brandPink" : "bg-white text-slate-700 border-slate-200 hover:border-brandPink"}`;
  const sizes = values.clothing_subcategory === "shoes_footwear"
    ? CLOTHING_FOOTWEAR_SIZES
    : CLOTHING_SIZE_BANDS;
  return (
    <div className="rounded-2xl border border-rose-200 bg-rose-50/50 p-3 space-y-3" data-testid={testIdPrefix}>
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500">What kind of clothing?</label>
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-subs`}>
          {CLOTHING_SUBCATEGORIES.map((s) => {
            const picked = values.clothing_subcategory === s.value;
            return (
              <button key={s.value} type="button"
                onClick={() => onChange({ clothing_subcategory: picked ? "" : s.value, clothing_size: "" })}
                className={chipCls(picked)}
                data-testid={`${testIdPrefix}-sub-${s.value}`}
                aria-pressed={picked}
              >{s.label}</button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Who is it for?</label>
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-genders`}>
          {CLOTHING_GENDERS.map((g) => {
            const picked = values.clothing_gender === g.value;
            return (
              <button key={g.value} type="button"
                onClick={() => onChange({ clothing_gender: picked ? "" : g.value })}
                className={chipCls(picked)}
                data-testid={`${testIdPrefix}-gender-${g.value}`}
                aria-pressed={picked}
              >{g.label}</button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Size</label>
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-sizes`}>
          {sizes.map((s) => {
            const picked = values.clothing_size === s;
            return (
              <button key={s} type="button"
                onClick={() => onChange({ clothing_size: picked ? "" : s })}
                className={chipCls(picked)}
                data-testid={`${testIdPrefix}-size-${slug(s)}`}
                aria-pressed={picked}
              >{s}</button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Brand</label>
        <div className="mt-2 flex flex-wrap gap-2" data-testid={`${testIdPrefix}-brands`}>
          {CLOTHING_BRANDS.map((b) => {
            const picked = values.clothing_brand === b;
            return (
              <button key={b} type="button"
                onClick={() => onChange({ clothing_brand: picked ? "" : b })}
                className={chipCls(picked)}
                data-testid={`${testIdPrefix}-brand-${slug(b)}`}
                aria-pressed={picked}
              >{b}</button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
